/**
 * SprintBurndownChart — Burndown du sprint actif
 *
 *  • Bornes X = date de début / fin du premier sprint actif
 *  • Baseline (J0) = US dont le sprint démarre à la date de début du sprint
 *  • Travail réalisé = US passées en test (PO / testeur / client) ou Done
 *    → leurs SP sont soustraits du restant (datés par date_test)
 *  • Clic sur un point → détail des US injectées et passées à test ce jour
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Zap, CheckCircle2, X, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';

const fmtAxis = d => { try { return format(parseISO(d), 'd/MM', { locale: fr }); } catch { return d; } };
const fmtLong = d => { try { return format(parseISO(d), 'd MMMM yyyy', { locale: fr }); } catch { return d; } };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const remaining = payload.find(p => p.dataKey === 'remaining');
  const ideal     = payload.find(p => p.dataKey === 'ideal');
  const point     = payload[0]?.payload || {};
  const date = label ? format(parseISO(label), 'd MMMM', { locale: fr }) : '';
  const nTest = point.tested?.length || 0;
  const spTest = point.tested?.reduce((s, t) => s + (t.sp || 0), 0) || 0;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[180px]">
      <p className="text-xs font-semibold text-gray-500 mb-2">{date}</p>
      {remaining?.value != null && (
        <div className="flex justify-between gap-4 text-xs mb-1">
          <span className="font-medium text-purple-700">SP restants</span>
          <span className="font-bold text-gray-800">{remaining.value}</span>
        </div>
      )}
      {ideal && (
        <div className="flex justify-between gap-4 text-xs">
          <span className="text-gray-400">Idéal</span>
          <span className="text-gray-500">{ideal.value}</span>
        </div>
      )}
      {nTest > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
          <span className="text-emerald-600">{nTest} US passée{nTest > 1 ? 's' : ''} à test · {spTest} SP traité{spTest > 1 ? 's' : ''}</span>
          <span className="block mt-0.5 italic">Cliquer pour le détail</span>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, sub, color = 'gray' }) => {
  const cls = {
    gray:   'bg-gray-50 text-gray-800',
    purple: 'bg-purple-50 text-purple-700',
    green:  'bg-emerald-50 text-emerald-600',
    red:    'bg-red-50 text-red-600',
  };
  return (
    <div className={`rounded-xl p-3 text-center ${cls[color]}`}>
      <p className="text-[10px] text-gray-400 mb-0.5">{label}</p>
      <p className={`text-xl font-bold ${cls[color].split(' ')[1]}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
};

const groupByDev = (items) => {
  const groups = {};
  items.forEach(us => {
    const devList = us.devs?.length > 0 ? us.devs : ['Non assigné'];
    devList.forEach(dev => {
      if (!groups[dev]) groups[dev] = [];
      groups[dev].push(us);
    });
  });
  return groups;
};

// Liste d'US groupée par développeur
const GroupedByDevList = ({ title, items, icon: Icon, accent }) => {
  const groups = useMemo(() => groupByDev(items), [items]);
  const c = { bg: 'bg-indigo-50', border: 'border-indigo-100', icon: 'text-indigo-500', text: 'text-indigo-700', sub: 'text-indigo-400' };
  const devNames = Object.keys(groups).sort((a, b) => {
    if (a === 'Non assigné') return 1;
    if (b === 'Non assigné') return -1;
    return a.localeCompare(b);
  });
  const totalSP = items.reduce((s, t) => s + (t.sp || 0), 0);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 mb-4">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <span className="text-xs text-gray-400">({items.length} · {totalSP} SP)</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-300 italic">Aucune</p>
      ) : (
        <div className="space-y-3">
          {devNames.map(dev => {
            const usForDev = groups[dev];
            const devSP = usForDev.reduce((s, u) => s + (u.sp || 0), 0);
            return (
              <div key={dev} className={`border ${c.border} rounded-xl overflow-hidden`}>
                <div className={`flex items-center gap-2 px-3 py-2 ${c.bg} border-b ${c.border}`}>
                  <User className={`w-3.5 h-3.5 ${c.icon} shrink-0`} />
                  <span className={`text-xs font-semibold ${c.text} flex-1 truncate`}>{dev}</span>
                  <span className={`text-xs ${c.sub} shrink-0`}>{usForDev.length} US · {devSP} SP</span>
                </div>
                <ul className="divide-y divide-gray-50">
                  {usForDev.map(us => (
                    <li key={us.id} className="flex items-start justify-between gap-3 text-sm px-3 py-2 bg-white hover:bg-gray-50 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-gray-700 text-xs" title={us.name}>{us.name}</p>
                        {us.stage && <span className="text-[11px] text-gray-400">{us.stage}</span>}
                      </div>
                      <span className="shrink-0 font-bold text-gray-600 tabular-nums text-xs">{us.sp} SP</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SprintBurndownChart = ({ sprintData }) => {
  const {
    sprintStart, sprintEnd,
    baselineSP = 0, completedSP = 0, remainingSP = 0,
    totalUS = 0, doneUS = 0, series = []
  } = sprintData || {};

  const [selectedDate, setSelectedDate] = useState(null);
  const pointModalExit = useAnimatedModalClose(() => setSelectedDate(null));
  const closePointAnimated = pointModalExit.close;

  const completionPct = totalUS > 0 ? Math.round((doneUS / totalUS) * 100) : 0;

  const lastReal = useMemo(
    () => [...series].reverse().find(p => p.remaining != null) || null,
    [series]
  );
  const isFinished = lastReal && remainingSP === 0;
  const isAhead    = !isFinished && lastReal && lastReal.remaining < lastReal.ideal;

  const selectedPoint = useMemo(
    () => series.find(p => p.date === selectedDate) || null,
    [series, selectedDate]
  );

  // Fermeture du popup avec Échap
  useEffect(() => {
    if (!selectedDate) return;
    const onKey = (e) => { if (e.key === 'Escape') closePointAnimated(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closePointAnimated, selectedDate]);

  const statusBadge = isFinished
    ? { label: 'Terminé',   bg: 'bg-emerald-50', text: 'text-emerald-700' }
    : isAhead
    ? { label: 'En avance', bg: 'bg-blue-50',    text: 'text-blue-700' }
    : { label: 'En cours',  bg: 'bg-orange-50',  text: 'text-orange-700' };

  const handleChartClick = (e) => {
    if (e?.activeLabel) setSelectedDate(prev => prev === e.activeLabel ? null : e.activeLabel);
  };

  if (!sprintStart || series.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <Zap className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Aucun sprint actif trouvé</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Burndown Chart — Sprint actif</h3>
            <p className="text-xs text-gray-500">
              {sprintStart && `du ${format(parseISO(sprintStart), 'd MMM', { locale: fr })}`}
              {sprintEnd   && ` au ${format(parseISO(sprintEnd),   'd MMM yyyy', { locale: fr })}`}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${statusBadge.bg} ${statusBadge.text}`}>
          {statusBadge.label}
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        <KpiCard label="SP engagés (J0)" value={baselineSP} color="purple" />
        <KpiCard label="SP complétés"    value={completedSP} color="green" />
        <KpiCard label="SP restants"     value={remainingSP} color={remainingSP === 0 ? 'green' : isAhead ? 'purple' : 'red'} />
        <KpiCard label="US terminées"    value={`${completionPct}%`} sub={`${doneUS}/${totalUS} US`} color={completionPct === 100 ? 'green' : 'purple'} />
      </div>

      {/* Graphique */}
      {series.length < 2 ? (
        <div className="flex items-center justify-center h-52 text-sm text-gray-400">
          Données insuffisantes (sprint trop récent)
        </div>
      ) : (
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={series}
              margin={{ top: 10, right: 16, left: -10, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmtAxis}
                tick={{ fontSize: 10, fill: '#9CA3AF' }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9CA3AF' }}
                axisLine={false} tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Ligne idéale (tirets gris) */}
              <Line
                type="linear" dataKey="ideal"
                stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="6 3"
                dot={false} name="Idéal"
              />

              {/* SP restants réels (violet) — points cliquables */}
              <Area
                type="monotone" dataKey="remaining"
                stroke="#6B4C9A" strokeWidth={2.5}
                fill="#EDE9FE" fillOpacity={0.5}
                connectNulls={false}
                dot={{ r: 3, fill: '#6B4C9A', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#6B4C9A', cursor: 'pointer' }}
                name="SP restants"
              />

              {selectedDate && <ReferenceLine x={selectedDate} stroke="#6B4C9A" strokeDasharray="4 2" strokeWidth={1.5} />}
              <ReferenceLine y={0} stroke="#E5E7EB" strokeWidth={1} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Légende */}
      <div className="flex items-center gap-5 mt-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="inline-block w-5 h-0.5 bg-purple-600 rounded" />
          SP restants réels
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="inline-block w-5 border-t border-dashed border-gray-400" />
          Idéal
        </div>
        <span className="text-[11px] text-gray-300 italic ml-auto">Cliquez sur un point pour voir le détail du jour</span>
      </div>

      {/* Popup de détail (jour sélectionné) — fermable via ✕ / Échap / clic backdrop */}
      {selectedPoint && (
        <div
          className={`${pointModalExit.modalClassName} fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm`}
          onClick={pointModalExit.close}
        >
          <div
            className="dashboard-modal__surface bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-purple-50 rounded-xl flex items-center justify-center">
                  <Zap className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-gray-900">{fmtLong(selectedPoint.date)}</h4>
                  {selectedPoint.remaining != null && (
                    <p className="text-xs text-gray-400">{selectedPoint.remaining} SP restants ce jour</p>
                  )}
                </div>
              </div>
              <button
                onClick={pointModalExit.close}
                className="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-gray-50"
                aria-label="Fermer (Échap)"
                title="Fermer (Échap)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Corps */}
            <div className="px-6 py-5 overflow-y-auto">
              <GroupedByDevList title="US passées à test ce jour" items={selectedPoint.tested || []} icon={CheckCircle2} accent="text-emerald-500" />
            </div>
            {/* Pied */}
            <div className="px-6 py-2.5 border-t border-gray-100 text-[11px] text-gray-400 text-right">
              Appuyez sur <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-sans">Échap</kbd> pour fermer
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SprintBurndownChart;
